import { CommonModule } from '@angular/common';
import { Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { Query } from '@damofire/data-commons';
import { ObservableSavedSearchService, SavedSearch, SavedSearchService, Warehouse, WarehouseInterceptorService } from '@damofire/data-watson';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AutoUnsubscribe } from '../../auto-unsubscribe.decorator';
import { FormFieldTextComponent } from '../../forms/form-field-text/form-field-text.component';
import { DialogService } from '../../popup/dialog-service/dialog.service';
import { SnackService } from '../../snack.service';

@AutoUnsubscribe()
@Component({
  selector: 'app-save-search-action',
  templateUrl: './save-search-action.component.html',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatDialogModule, MatIconModule, MatMenuModule, FormFieldTextComponent, TranslatePipe],
})
export class SaveSearchActionComponent {
  @Input()
  disabled = false;

  private warehouse: Warehouse;

  @Input()
  editMode = false;

  @Input()
  queryCallback: () => Query;

  @ViewChild('addSavedSearch')
  addSavedSearch: TemplateRef<HTMLElement>;

  @Input()
  savedSearch: SavedSearch;

  form: FormGroup;

  private existingSearches: SavedSearch[] = [];
  private sub: Subscription;

  constructor(
    private readonly savedSearchService: SavedSearchService,
    private readonly warehouseInterceptorService: WarehouseInterceptorService,
    private readonly snackService: SnackService,
    private readonly dialog: DialogService,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly observableSavedSearchService: ObservableSavedSearchService,
  ) {
    warehouseInterceptorService.$warehouse.pipe(take(1)).subscribe((w) => {
      this.warehouse = w;
      this.sub?.unsubscribe();
      this.sub = this.observableSavedSearchService.list(w._id).subscribe((ss) => (this.existingSearches = ss));
    });
  }

  async show(): Promise<void> {
    this.form = this.formBuilder.group({ name: [this.savedSearch?.name || '', [Validators.required, Validators.maxLength(50), this.validateDuplicate()]] });
    const name = await this.dialog.openTemplate<string>(this.addSavedSearch, { width: '400px' });
    if (name) {
      if (!this.editMode) {
        const { division, organization } = this.warehouse;
        const savedSearch = await this.savedSearchService.create({
          type: 'issues',
          query: this.queryCallback(),
          division,
          organization,
          system: false,
          name,
        });
        this.snackService.snackThis('commons.messages.create.success', { name });
        await this.router.navigate([], { queryParams: { search: savedSearch._id } });
      } else {
        await this.savedSearchService.update({ ...this.savedSearch, name });
        this.snackService.snackThis('commons.messages.update.success', { name });
      }
    }
  }

  async update(): Promise<void> {
    await this.savedSearchService.update({ ...this.savedSearch, query: this.queryCallback() });
    this.snackService.snackThis('commons.messages.update.success', { name: this.savedSearch.name });
  }

  closeDialog(): void {
    this.dialog.close(this.form.controls['name'].value);
  }

  cancelDialog(): void {
    this.dialog.closeAll();
  }

  private validateDuplicate(): (ctrl: AbstractControl) => ValidationErrors {
    return (ctrl: AbstractControl) => {
      let errors = null;

      if (ctrl.value && (ctrl.value !== this.savedSearch?.name || !this.editMode)) {
        const matching = this.existingSearches.filter((ss) => ss.name?.toLowerCase() === ctrl.value.toLowerCase());

        if (matching.length) {
          errors = { duplicate: true };
        }
      }
      return errors;
    };
  }
}
